
import React from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import CountrySelector from "@/components/ui/CountrySelector";
import { toast } from "sonner";
import { submitPendingUniversity } from "@/services/pendingUniversityService";
import { University } from "@/services/universityService";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export interface UniversityFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (university: University) => void;
}

export interface UniversityFormValues {
  name: string;
  address: string;
  city: string;
  postcode: string;
  country: string;
}

const UniversityForm: React.FC<UniversityFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  
  const form = useForm<UniversityFormValues>({
    defaultValues: {
      name: "",
      address: "",
      city: "",
      postcode: "",
      country: ""
    }
  });

  const handleClose = () => {
    setSubmitted(false);
    form.reset();
    onClose();
  };
  
  const handleAddUniversity = async (data: UniversityFormValues) => {
    setIsLoading(true);
    
    try {
      const result = await submitPendingUniversity({
        name: data.name,
        address: data.address || null,
        city: data.city,
        postcode: data.postcode || null,
        country: data.country
      });
      
      if (result) {
        toast.success("University submitted for approval");
        setSubmitted(true);
      } else {
        toast.error("Failed to submit university");
      }
    } catch (error) {
      console.error("Error submitting university:", error);
      toast.error("An error occurred while submitting the university");
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>University Submitted</DialogTitle>
            <DialogDescription>
              Your university has been submitted for review.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                An administrator will review your submission and approve it if it meets our criteria. 
                Once approved, the university will be available for selection.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button onClick={handleClose}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New University/Institution</DialogTitle>
          <DialogDescription>
            Submit a new university for admin approval. It will be available once approved.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(handleAddUniversity)} className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" placeholder="University name" {...form.register("name")} required />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input id="address" placeholder="Street address" {...form.register("address")} />
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <Input id="city" placeholder="City" {...form.register("city")} required />
            </div>
            <div>
              <Label htmlFor="postcode">Postcode</Label>
              <Input id="postcode" placeholder="Postcode" {...form.register("postcode")} />
            </div>
            <div>
              <Label htmlFor="country">Country</Label>
              <CountrySelector 
                value={form.watch("country") || ""}
                onValueChange={(value) => form.setValue("country", value)}
                placeholder="Select country"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Submitting..." : "Submit for Approval"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UniversityForm;
